import type { ChangeWorkGroup } from '../../types/changes';
import { changesApi } from '../../services/changesApi';
import { parseQuestionOptions, type ReviewQuestionDraft } from './changeInstanceChecklistFields';
import type { InternalReviewDraft } from './components/ChangeCreateSubtaskReview';

export type InternalSubtaskTemplatePersistMode = 'global' | 'personal';

function fieldPayloadFromQuestion(question: ReviewQuestionDraft): Record<string, unknown> {
  return {
    label: question.label,
    field_type: question.field_type,
    is_required: question.is_required,
    help_text: question.help_text ?? null,
    col_span: question.col_span ?? 12,
    section_name: question.section_name ?? null,
    period_mode: question.period_mode ?? null,
    weekday_constraint: question.weekday_constraint ?? null,
    options:
      question.field_type === 'list' || question.field_type === 'yes_no'
        ? parseQuestionOptions(question.options)
        : [],
    default_value: question.default_value ?? null,
  };
}

async function replaceTemplateFields(templateId: number, questions: ReviewQuestionDraft[]): Promise<void> {
  await changesApi.replaceInternalSubtaskTemplateCustomFields(
    templateId,
    questions.map((question) => fieldPayloadFromQuestion(question)),
  );
}

export async function persistInternalSubtaskTemplateFromDraft(opts: {
  name: string;
  groupId: number;
  questions: ReviewQuestionDraft[];
  mode: InternalSubtaskTemplatePersistMode;
  templateId?: number | null;
  defaultTitle?: string | null;
}): Promise<number> {
  const trimmed = opts.name.trim();
  if (!trimmed) {
    throw new Error('Informe o nome do modelo.');
  }
  if (!opts.questions.length) {
    throw new Error('Inclua ao menos um campo antes de salvar o modelo.');
  }
  const asPersonal = opts.mode === 'personal';
  let templateId = opts.templateId && opts.templateId > 0 ? opts.templateId : null;

  if (templateId) {
    // Título da instância (default_title) ≠ nome do catálogo; renomear o modelo aqui
    // colide com outro template global/pessoal e não é o que o usuário espera no pacote.
    await changesApi.updateInternalSubtaskTemplate(templateId, {
      default_title: opts.defaultTitle?.trim() || trimmed || null,
      show_on_dispatch: true,
      is_active: true,
    });
    await changesApi.replaceInternalSubtaskTemplateGroups(templateId, [opts.groupId]);
    await replaceTemplateFields(templateId, opts.questions);
    return templateId;
  }

  const created = await changesApi.createInternalSubtaskTemplate({
    name: trimmed,
    default_title: opts.defaultTitle?.trim() || null,
    group_ids: [opts.groupId],
    show_on_dispatch: true,
    as_personal: asPersonal,
  });
  templateId = created.id;
  for (const question of opts.questions) {
    await changesApi.createInternalSubtaskTemplateField(templateId, fieldPayloadFromQuestion(question));
  }
  return templateId;
}

export async function deletePersistedInternalSubtaskTemplate(templateId: number): Promise<void> {
  await changesApi.deleteInternalSubtaskTemplate(templateId);
}

export function resolveInternalTemplateNameFromDraft(
  draft: Pick<InternalReviewDraft, 'title'>,
  slot: ChangeWorkGroup,
): string {
  return (
    (draft.title || '').trim() ||
    (slot.internal_subtask_template_name || '').trim() ||
    (slot.name || '').trim()
  );
}

/** Grava modelo reutilizável quando há campos montados (modal / pacote). */
export async function persistInternalDraftIfNeeded(opts: {
  slot: ChangeWorkGroup;
  draft: InternalReviewDraft;
  mode: InternalSubtaskTemplatePersistMode;
  canPersist: boolean;
}): Promise<number | null> {
  if (!opts.canPersist) return null;
  const questions = opts.draft.questions || [];
  if (!questions.length) return null;
  const name = resolveInternalTemplateNameFromDraft(opts.draft, opts.slot);
  if (!name) {
    throw new Error('Informe o título da subtarefa ou o nome do modelo antes de salvar.');
  }
  const templateId = opts.slot.internal_subtask_template_id;
  return persistInternalSubtaskTemplateFromDraft({
    name,
    groupId: opts.slot.group_id,
    questions,
    mode: opts.mode,
    templateId: templateId && templateId > 0 ? templateId : undefined,
    defaultTitle: (opts.draft.title || '').trim() || null,
  });
}
